import { useState } from "react";
import { ListX, ScrollText, Users } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import DocsContent from "../../components/DocsContent";
import { CopyButton, Card, EmptyState, ErrorBanner, Notice, PageHeader, Spinner, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { AXIS_TICK, BAR_RADIUS, INITIAL_DIM, TOOLTIP_STYLE } from "../../lib/chart";
import { UsageBar } from "./Dashboard";
import { UploadBox } from "./Files";

const ROLES = ["owner", "admin", "developer", "read_only"];
const roleLabel = (r) => r.replace("_", "-");

export function Suppressions() {
  const { t } = useT();
  const { can } = useAuth();
  const [email, setEmail] = useState("");
  const [q, setQ] = useState("");
  const { data, error, loading, reload } = useLoad(() => api.get("/app/suppressions", { params: { q: q || undefined } }).then((r) => r.data), [q]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/app/suppressions", { email }); toast.success("Address suppressed"); setEmail(""); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (em) => {
    if (!window.confirm(`Remove ${em} from the suppression list? Only do this if the recipient asked to receive email again.`)) return;
    try { await api.delete(`/app/suppressions/${encodeURIComponent(em)}`); toast.success("Removed"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <PageHeader title={t("nav.suppressions")} description={t("desc.suppressions")} />
      <div className="mb-4 flex flex-col gap-3 lg:flex-row">
        {can("admin") && <form onSubmit={add} className="flex flex-1 gap-2"><Input type="email" required placeholder="address@example.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Address to suppress" data-testid="suppression-email" />
          <Button type="submit" data-testid="suppression-add">Suppress</Button></form>}
        <Input placeholder="Search" className="lg:w-64" onChange={(e) => setQ(e.target.value)} aria-label="Search suppressions" data-testid="suppression-search" />
      </div>
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (data.length === 0 ? <EmptyState icon={ListX} title="No suppressed addresses" testId="suppressions-empty" /> : (
        <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full text-left text-sm" data-testid="suppressions-table">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Address</th><th className="p-3">Reason</th><th className="p-3">Added</th><th /></tr></thead>
          <tbody>{data.map((s) => (
            <tr key={s.email} className="border-t border-white/5"><td className="p-3 font-mono text-xs text-zinc-200">{s.email}</td><td className="p-3 text-xs text-zinc-400">{s.reason.replace("_", " ")}</td>
              <td className="p-3 text-xs text-zinc-500">{fmt(s.created_at)}</td>
              <td className="p-3 text-right">{can("admin") && <Button size="sm" variant="ghost" onClick={() => remove(s.email)} className="text-xs hover:bg-white/5 hover:text-white" data-testid={`suppression-remove-${s.email}`}>Remove</Button>}</td></tr>
          ))}</tbody></table></div>
      ))}
    </div>
  );
}

export function Usage() {
  const { t } = useT();
  const { data, error, loading } = useLoad(() => api.get("/app/usage").then((r) => r.data));
  return (
    <div>
      <PageHeader title={t("nav.usage")} description={t("desc.usage")} />
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (
        <div className="grid gap-6 lg:grid-cols-3">
          <Card testId="usage-limits"><div className="space-y-5">
            <UsageBar label="Today" used={data.today} limit={data.limits.daily} testId="usage-page-today" />
            <UsageBar label="This month" used={data.month} limit={data.limits.monthly} testId="usage-page-month" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-zinc-400" data-testid="usage-page-rate">
              {[["Per second", data.limits.per_second], ["Per minute", data.limits.per_minute], ["Per hour", data.limits.per_hour], ["Rate-limit hits (30 days)", data.rate_limited_30d]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-zinc-500">{k}</div><div className="font-mono text-white">{data.limits.unlimited && k !== "Rate-limit hits (30 days)" ? "∞" : (v ?? "—")}</div></div>))}
            </div>
            <div className="text-xs text-zinc-500">Max 50 recipients and 10 MB per message. <a href="/app/suppressions" className="text-purple-300 underline">{data.suppressed} suppressed address(es)</a></div>
            {data.sandbox && <Notice tone="warn" title="Sandbox account" testId="usage-sandbox">New accounts can send 50 emails a day, only to verified team members. Limits lift when you upgrade, when an admin approves the account, or automatically after 7 days of clean sending.</Notice>}
            <p className="text-xs text-zinc-500">Limits are set by platform administrators. Requests beyond them receive HTTP 429.</p>
          </div></Card>
          <Card className="lg:col-span-2" testId="usage-chart">
            <div className="mb-4 text-sm font-medium text-white">Last 30 days</div>
            {data.daily.length ? <div className="h-56"><ResponsiveContainer initialDimension={INITIAL_DIM}><BarChart data={data.daily}>
              <XAxis dataKey="day" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="sent" fill="#9333EA" radius={BAR_RADIUS} /></BarChart></ResponsiveContainer></div>
              : <p className="py-12 text-sm text-zinc-500">No live usage yet.</p>}
          </Card>
        </div>
      )}
    </div>
  );
}

export function AuditLog() {
  const { t } = useT();
  const { data, error, loading } = useLoad(() => api.get("/app/audit").then((r) => r.data));
  return (
    <div>
      <PageHeader title={t("nav.audit")} description={t("desc.audit")} />
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (data.length === 0 ? <EmptyState icon={ScrollText} title="No audit events" /> : (
        <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full min-w-[680px] text-left text-sm" data-testid="audit-table">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Action</th><th className="p-3">Actor</th><th className="p-3">Target</th><th className="p-3">Request</th><th className="p-3">Time</th></tr></thead>
          <tbody>{data.map((a) => (
            <tr key={a.id} className="border-t border-white/5"><td className="p-3 font-mono text-xs text-purple-200">{a.action}</td><td className="p-3 text-xs text-zinc-300">{a.actor_email || a.actor_id || "system"}</td>
              <td className="p-3 font-mono text-[11px] text-zinc-500">{a.target || "—"}</td><td className="p-3 font-mono text-[11px] text-zinc-600">{a.request_id}</td><td className="whitespace-nowrap p-3 text-xs text-zinc-500">{fmt(a.created_at)}</td></tr>
          ))}</tbody></table></div>
      ))}
    </div>
  );
}

export function Team() {
  const { t } = useT();
  const { can } = useAuth();
  const [form, setForm] = useState({ email: "", role: "developer" });
  const { data, error, loading, reload } = useLoad(() => api.get("/app/team").then((r) => r.data));
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const invite = (e) => { e.preventDefault(); act(() => api.post("/app/team/invites", form), "Invitation saved").then(() => setForm({ email: "", role: "developer" })); };
  return (
    <div>
      <PageHeader title={t("nav.team")} description={t("desc.team")} />
      <ErrorBanner error={error} />
      {can("admin") && (
        <form onSubmit={invite} className="mb-6 flex flex-col gap-2 sm:flex-row" data-testid="team-invite-form">
          <Input type="email" required placeholder="teammate@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="Email" data-testid="invite-email" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="sm:w-40" data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent></Select>
          <Button type="submit" data-testid="invite-submit">Invite</Button>
        </form>
      )}
      {loading ? <Spinner /> : data && (
        <div className="space-y-6">
          <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full text-left text-sm" data-testid="team-table">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Member</th><th className="p-3">Role</th><th className="p-3">Joined</th><th /></tr></thead>
            <tbody>{data.members.map((m) => (
              <tr key={m.user_id} className="border-t border-white/5">
                <td className="p-3"><div className="text-zinc-200">{m.name}</div><div className="text-xs text-zinc-500">{m.email}</div></td>
                <td className="p-3">{can("admin") && m.user_id !== data.my_id ? (
                  <Select value={m.role} onValueChange={(v) => act(() => api.patch(`/app/team/members/${m.user_id}`, { role: v }), "Role updated")}>
                    <SelectTrigger className="h-8 w-36" data-testid={`member-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent></Select>
                ) : <span className="text-xs text-zinc-300">{roleLabel(m.role)}{m.user_id === data.my_id && " (you)"}</span>}</td>
                <td className="p-3 text-xs text-zinc-500">{fmt(m.joined_at)}</td>
                <td className="p-3 text-right">{can("admin") && m.user_id !== data.my_id && <Button size="sm" variant="ghost" className="text-xs text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  onClick={() => window.confirm(`Remove ${m.email}?`) && act(() => api.delete(`/app/team/members/${m.user_id}`), "Member removed")} data-testid={`member-remove-${m.user_id}`}>Remove</Button>}</td>
              </tr>))}</tbody></table></div>
          {data.invites.length > 0 && <Card testId="pending-invites"><div className="mb-3 text-sm font-medium text-white">Pending invites</div>
            {data.invites.map((i) => <div key={i.email} className="flex items-center justify-between border-t border-white/5 py-2 text-sm first:border-0">
              <span className="text-zinc-300">{i.email} · <span className="text-zinc-500">{roleLabel(i.role)}</span></span>
              {can("admin") && <Button size="sm" variant="ghost" className="text-xs hover:bg-white/5 hover:text-white" onClick={() => act(() => api.delete(`/app/team/invites/${encodeURIComponent(i.email)}`), "Invite revoked")}>Revoke</Button>}
            </div>)}</Card>}
          {data.members.length === 1 && data.invites.length === 0 && <EmptyState icon={Users} title="Just you so far" body="Invite teammates with the role they need." />}
        </div>
      )}
    </div>
  );
}

function LogoCard({ ws, canEdit, onSaved }) {
  const { t } = useT();
  const set = async (file_id) => {
    try { await api.put("/app/workspace/logo", { file_id }); toast.success(file_id ? "Logo updated" : "Logo removed"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testId="settings-logo">
      <div className="text-sm font-medium text-white">{t("Workspace logo")}</div>
      <p className="mt-1 text-xs text-zinc-500">{t("Shown in the app sidebar and in the header of invite, usage and receipt emails for this workspace. PNG, JPEG or WebP, up to 2 MB.")}</p>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
          {ws.logo_url ? <img src={ws.logo_url} alt="Workspace logo" className="max-h-14 max-w-14 object-contain" data-testid="settings-logo-image" /> : <span className="text-xs text-zinc-600">none</span>}
        </div>
        {canEdit && <div className="flex gap-2"><UploadBox kind="logo" compact testId="settings-logo-upload" onDone={(f) => set(f.id)} />
          {ws.logo_url && <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10 hover:text-red-200" onClick={() => set(null)} data-testid="settings-logo-remove">{t("Remove")}</Button>}</div>}
      </div>
      {!canEdit && <p className="mt-2 text-xs text-zinc-500">{t("Only admins and owners can change the logo.")}</p>}
    </Card>
  );
}

function UsageAlertsCard({ ws, canEdit, onSaved }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try { await api.patch("/app/workspace/alerts", { usage_alerts_admins: v }); toast.success("Usage alert recipients updated"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testId="settings-usage-alerts">
      <div className="text-sm font-medium text-white">{t("Usage warning emails")}</div>
      <p className="mt-1 text-xs text-zinc-500">{t("Sent once a month at 80% and at 100% of the monthly plan limit. Owners always receive them.")}</p>
      <label className="mt-4 flex items-center gap-3 text-sm text-zinc-300">
        <Switch checked={ws.usage_alerts_admins} disabled={!canEdit || busy} onCheckedChange={toggle} data-testid="settings-usage-alerts-admins" />
        {t("Also send to admins")}
      </label>
      {!canEdit && <p className="mt-2 text-xs text-zinc-500">{t("Only owners can change this.")}</p>}
    </Card>
  );
}

function SmtpGatewayInfo({ gw }) {
  if (!gw.publicly_reachable) return <div className="mt-4"><Notice tone="warn" title="SMTP: not live yet" testId="smtp-gateway-offline">{gw.reason}</Notice></div>;
  const config = `Host: ${gw.public_host}\nPort: 587 (STARTTLS) · 465 (SSL/TLS) · 2525 (STARTTLS fallback)\nUsername: cario\nPassword: <your CARIO API key with emails:send>\nEncryption: required (AUTH is refused before TLS)`;
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="smtp-gateway-info">
      <div className="mb-2 flex items-center justify-between text-sm font-medium text-white">
        <span>SMTP relay <span className="text-emerald-300">· live</span></span><CopyButton value={config} label="Copy settings" testId="smtp-config-copy" /></div>
      <dl className="grid gap-1 font-mono text-xs text-zinc-300 sm:grid-cols-2">
        <div>Host: <span data-testid="smtp-gateway-host">{gw.public_host}</span></div>
        <div>Ports: {gw.ports.map((p) => `${p.port} (${p.security})`).join(" · ")}</div>
        <div>Username: cario</div><div>Password: your API key (emails:send)</div>
      </dl>
      <p className="mt-2 text-xs text-zinc-500">{gw.auth} Max message size {gw.max_message_mb} MB. <a href="/docs/smtp" className="text-purple-300 underline">SMTP guide</a></p>
    </div>
  );
}

export function SettingsPage() {
  const { t, lang, setLang } = useT();
  const { can, reload: reloadUser, user } = useAuth();
  const { data, error, loading, reload } = useLoad(() => api.get("/app/workspace").then((r) => r.data));
  const status = useLoad(() => api.get("/app/sending-status").then((r) => r.data));
  const [name, setName] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch("/app/workspace", { name }); toast.success("Workspace renamed"); reload(); reloadUser(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <PageHeader title={t("nav.settings")} description={t("desc.settings")} />
      <ErrorBanner error={error} />
      {loading && !data ? <Spinner /> : data && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card testId="settings-workspace">
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-1.5"><Label htmlFor="wsname">{t("Workspace name")}</Label>
                <Input id="wsname" value={name ?? data.name} onChange={(e) => setName(e.target.value)} disabled={!can("admin")} required maxLength={80} data-testid="settings-workspace-name" /></div>
              {can("admin") && <Button type="submit" disabled={!name} data-testid="settings-save">{t("common.save")}</Button>}
            </form>
            <dl className="mt-6 space-y-1 text-xs text-zinc-500"><div>ID <span className="font-mono text-zinc-400">{data.id}</span></div><div>{t("Your role")}: {t(`role.${data.role}`)}</div><div>{t("Signed in as")} {user?.email}</div></dl>
          </Card>
          <Card testId="settings-language">
            <Label>Language / Kieli</Label>
            <Select value={lang} onValueChange={setLang}><SelectTrigger className="mt-2 w-48" data-testid="settings-language-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="fi">Suomi</SelectItem></SelectContent></Select>
          </Card>
          <UsageAlertsCard ws={data} canEdit={can("owner")} onSaved={reload} />
          <LogoCard ws={data} canEdit={can("admin")} onSaved={() => { reload(); reloadUser(); }} />
          {status.data && <Card className="lg:col-span-2" testId="settings-sending">
            <div className="mb-3 text-sm font-medium text-white">{t("Sending configuration")}</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li>REST API: <span className="text-emerald-300">available</span></li>
              <li>Provider: {status.data.configured ? status.data.provider : <span className="text-red-300">{t("notconfigured")}</span>}</li>
              <li>Delivery events: {status.data.delivery_events_configured ? "configured" : <span className="text-amber-300">not configured — final delivery status stays unknown</span>}</li>
            </ul>
            <SmtpGatewayInfo gw={status.data.smtp_gateway} />
          </Card>}
        </div>
      )}
    </div>
  );
}

export function AppDocs() {
  const { t } = useT();
  return <div><PageHeader title={t("nav.docs")} description={t("desc.docs")} /><DocsContent /></div>;
}
